/**
 * WhatsApp channel — per-page components for the domain-app shell.
 *
 * Each page is a thin wrapper around the existing, self-contained 360dialog tab
 * components (which already use the useDialog360 hook internally). The per-channel
 * left sidebar (provider `comms-whatsapp`) drives navigation, so these pages no
 * longer need the old Tabs control.
 *
 * Dashboard / Bulk Send / Settings have no pre-existing component yet — they render
 * a lightweight placeholder and are enriched in later phases (analytics, campaigns,
 * provider settings).
 */

import { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconMessage,
  IconSend,
  IconInbox,
  IconCloud,
  IconAddressBook,
  IconActivity,
  IconUsersGroup,
  IconSettings,
  IconLayoutDashboard,
  IconSpeakerphone,
  IconArrowLeft,
  IconReceipt2,
} from '@tabler/icons-react';
import { Stack, Group, Text, Title, Button } from '../../../../components/ui';
import { LayoutContainer } from '../../../../components/ui/layout';
import { FadeIn } from '../../../../components/animations/transitions';
import { ChannelDashboard } from '../_shared/ChannelDashboard';
import { ProviderSettingsForm } from '../_shared/ProviderSettingsForm';
import { BulkSendWizard } from '../_shared/BulkSendWizard';
import { CampaignsList, CampaignDetail } from '../_shared/CampaignsView';
import {
  Dialog360OutboundTab,
  Dialog360CloudTemplatesTab,
  Dialog360ProfileTab,
  Dialog360DiagnosticsTab,
} from './components';
import { Dialog360TemplateCompose } from './components/Dialog360TemplateCompose';
import { Dialog360BillingTab } from './components/Dialog360BillingTab';
import { Dialog360ConfigPanel } from './components/Dialog360ConfigPanel';
import { ConversationView } from '../_shared/ConversationView';

/** Shared page shell: header (icon + title + subtitle) over the page content. */
function ChannelPage({
  icon,
  title,
  subtitle,
  onBack,
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  children: ReactNode;
}) {
  return (
    <LayoutContainer py="xl">
      <FadeIn delay={80}>
        <Stack gap="lg">
          {onBack && (
            <Button variant="subtle" size="xs" px={4} leftSection={<IconArrowLeft size={16} />} onClick={onBack} style={{ alignSelf: 'flex-start' }}>
              Back
            </Button>
          )}
          <Stack gap={4}>
            <Group gap="sm">
              {icon}
              <Title order={2}>{title}</Title>
            </Group>
            {subtitle && (
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            )}
          </Stack>
          {children}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}

export function WhatsAppDashboardPage() {
  return (
    <ChannelPage
      icon={<IconLayoutDashboard size={28} />}
      title="WhatsApp"
      subtitle="Overview of the 360dialog WhatsApp integration. Use the left menu to compose, review the outbox/inbox, manage templates, and configure the channel."
    >
      <ChannelDashboard channel="whatsapp" />
    </ChannelPage>
  );
}

export function WhatsAppComposePage() {
  return (
    <ChannelPage icon={<IconMessage size={28} />} title="Compose" subtitle="Send an approved WhatsApp template message.">
      <Dialog360TemplateCompose />
    </ChannelPage>
  );
}

export function WhatsAppBulkSendPage() {
  return (
    <ChannelPage icon={<IconUsersGroup size={28} />} title="Bulk Send" subtitle="Send to many recipients via a campaign.">
      <BulkSendWizard channel="whatsapp" />
    </ChannelPage>
  );
}

export function WhatsAppCampaignsPage() {
  const navigate = useNavigate();
  return (
    <ChannelPage icon={<IconSpeakerphone size={28} />} title="Campaigns" subtitle="Bulk campaign history with delivery counts and per-recipient status.">
      <CampaignsList channel="whatsapp" onOpen={(id) => navigate(`/admin/communications/whatsapp/campaigns/${id}`)} />
    </ChannelPage>
  );
}

export function WhatsAppCampaignDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  return (
    <ChannelPage
      icon={<IconSpeakerphone size={28} />}
      title="Campaigns"
      subtitle="Delivery counts and per-recipient status."
      onBack={() => navigate('/admin/communications/whatsapp/campaigns')}
    >
      <CampaignDetail campaignId={Number(id)} />
    </ChannelPage>
  );
}

export function WhatsAppOutboxPage() {
  return (
    <ChannelPage icon={<IconSend size={28} />} title="Outbox" subtitle="Queued & sent WhatsApp messages with delivery status.">
      <Dialog360OutboundTab />
    </ChannelPage>
  );
}

export function WhatsAppInboxPage() {
  return (
    <ChannelPage icon={<IconInbox size={28} />} title="Inbox" subtitle="Two-way conversations with contacts — view threads and reply.">
      <ConversationView />
    </ChannelPage>
  );
}

export function WhatsAppTemplatesPage() {
  return (
    <ChannelPage icon={<IconCloud size={28} />} title="Templates" subtitle="360dialog message templates (create, edit, delete, approval status).">
      <Dialog360CloudTemplatesTab />
    </ChannelPage>
  );
}

export function WhatsAppProfilePage() {
  return (
    <ChannelPage icon={<IconAddressBook size={28} />} title="Profile" subtitle="WhatsApp business profile and phone numbers.">
      <Dialog360ProfileTab />
    </ChannelPage>
  );
}

export function WhatsAppBillingPage() {
  return (
    <ChannelPage icon={<IconReceipt2 size={28} />} title="Billing" subtitle="WhatsApp conversation spend pulled from 360dialog (Meta rate cards).">
      <Dialog360BillingTab />
    </ChannelPage>
  );
}

export function WhatsAppDiagnosticsPage() {
  return (
    <ChannelPage icon={<IconActivity size={28} />} title="Diagnostics" subtitle="Connection health and capability checks.">
      <Dialog360DiagnosticsTab />
    </ChannelPage>
  );
}

export function WhatsAppSettingsPage() {
  return (
    <ChannelPage icon={<IconSettings size={28} />} title="Settings" subtitle="Provider selection and channel configuration.">
      <Stack gap="lg">
        <Dialog360ConfigPanel />
        <ProviderSettingsForm channel="whatsapp" />
      </Stack>
    </ChannelPage>
  );
}

export { IconBrandWhatsapp };
